import React, { useState, useEffect } from 'react';
import {
  Chip, Tooltip, Popover, Box, Typography, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, Table, TableHead, TableRow, TableCell, TableBody,
  CircularProgress, ListItemButton, ListItemIcon, ListItemText
} from '@mui/material';
import {
  ClipboardList, Code2, GitBranch, ShieldCheck, Rocket, CheckCircle2, History, ArrowRight
} from 'lucide-react';
import axios from 'axios';
import API_BASE_URL from '../../Config/config';

/**
 * Project Status Management — shared SDLC status registry + UI building blocks.
 * Single source of truth for status metadata across the Admin Console.
 */

export const PROJECT_STATUSES = ['PLANNING', 'DEVELOPMENT', 'INTEGRATION', 'QA', 'RELEASE', 'COMPLETED'];

export const STATUS_META = {
  PLANNING: {
    label: 'Planning',
    description: 'Requirements gathering, architecture and sprint planning.',
    icon: ClipboardList,
    fg: '#334155', bg: '#f1f5f9', border: '#cbd5e1', solid: '#64748b'          // Slate
  },
  DEVELOPMENT: {
    label: 'Development',
    description: 'Core implementation and feature development.',
    icon: Code2,
    fg: '#1d4ed8', bg: '#dbeafe', border: '#93c5fd', solid: '#3b82f6'          // Blue
  },
  INTEGRATION: {
    label: 'Integration & CI/CD',
    description: 'Integration completed and CI/CD pipeline execution.',
    icon: GitBranch,
    fg: '#4338ca', bg: '#e0e7ff', border: '#a5b4fc', solid: '#6366f1'          // Indigo
  },
  QA: {
    label: 'Quality Assurance',
    description: 'Testing, bug fixing and validation.',
    icon: ShieldCheck,
    fg: '#b45309', bg: '#fef3c7', border: '#fcd34d', solid: '#f59e0b'          // Amber
  },
  RELEASE: {
    label: 'Release Preparation',
    description: 'Deployment readiness, documentation and final approval.',
    icon: Rocket,
    fg: '#7e22ce', bg: '#f3e8ff', border: '#d8b4fe', solid: '#a855f7'          // Purple
  },
  COMPLETED: {
    label: 'Completed',
    description: 'Successfully delivered and available for students.',
    icon: CheckCircle2,
    fg: '#166534', bg: '#dcfce7', border: '#86efac', solid: '#22c55e'          // Green
  }
};

const LEGACY_MAP = {
  'planning': 'PLANNING',
  'in progress': 'DEVELOPMENT', 'in_progress': 'DEVELOPMENT', 'active': 'DEVELOPMENT', 'development': 'DEVELOPMENT',
  'integration': 'INTEGRATION', 'integration & ci/cd': 'INTEGRATION',
  'under review': 'QA', 'review': 'QA', 'qa': 'QA', 'testing': 'QA',
  'release': 'RELEASE', 'release preparation': 'RELEASE',
  'completed': 'COMPLETED', 'inactive': 'COMPLETED'
};

export const normalizeStatus = (raw) => {
  if (!raw) return 'PLANNING';
  const s = String(raw).trim();
  if (PROJECT_STATUSES.includes(s.toUpperCase())) return s.toUpperCase();
  return LEGACY_MAP[s.toLowerCase()] || 'PLANNING';
};

/** Logged-in user identity + admin capability (platform convention: localStorage user.role). */
export const getAdminIdentity = () => {
  let user = {};
  try { user = JSON.parse(localStorage.getItem('user') || '{}'); } catch (e) { /* noop */ }
  const role = String(user?.role || '').toLowerCase();
  const isSuperAdmin = ['superadmin', 'super_admin', 'super admin', 'super-admin'].includes(role);
  const isAdmin = isSuperAdmin || ['admin', 'administrator'].includes(role);
  return {
    name: user?.username || user?.email || user?.full_name || (isAdmin ? 'admin' : ''),
    role,
    isAdmin,
    isSuperAdmin
  };
};

/** Headers the status API uses for RBAC. */
export const adminHeaders = () => {
  const id = getAdminIdentity();
  return { 'x-admin-user': id.name || 'admin', 'x-admin-role': id.role || '' };
};

/**
 * CORS-proof admin identity — sent as query params (GET) or body fields
 * (PATCH/POST) so a stale CORS allow-list can never block the preflight.
 */
export const adminIdentityParams = () => {
  const id = getAdminIdentity();
  return { adminUser: id.name || 'admin', adminRole: id.role || '' };
};

/* ── Status Chip ─────────────────────────────────────────────────────────── */

export const ProjectStatusChip = ({ status, clickable = false, onClick, size = 'small', loading = false }) => {
  const key = normalizeStatus(status);
  const meta = STATUS_META[key];
  const Icon = meta.icon;
  return (
    <Tooltip title={meta.description} arrow>
      <span>
        <Chip
          label={meta.label}
          size={size}
          clickable={clickable}
          disabled={loading}
          onClick={clickable ? onClick : undefined}
          onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(e); } } : undefined}
          icon={loading
            ? <CircularProgress size={13} sx={{ color: `${meta.fg} !important`, ml: '4px' }} />
            : <Icon size={14} style={{ color: meta.fg, marginLeft: 6 }} aria-hidden="true" />}
          aria-label={`Project status: ${meta.label}${clickable ? ' — click to change' : ''}`}
          sx={{
            bgcolor: meta.bg,
            color: meta.fg,
            border: `1px solid ${meta.border}`,
            fontWeight: 700,
            borderRadius: '8px',
            px: 0.5,
            '& .MuiChip-label': { px: 1 },
            ...(clickable && {
              cursor: 'pointer',
              transition: 'all .15s ease',
              '&:hover': { bgcolor: meta.border, boxShadow: `0 2px 8px ${meta.border}` }
            })
          }}
        />
      </span>
    </Tooltip>
  );
};

/* ── Status Select Popover (Option 1 edit experience) ────────────────────── */

export const StatusSelectPopover = ({ anchorEl, currentStatus, onClose, onSelect }) => {
  const current = normalizeStatus(currentStatus);
  return (
    <Popover
      open={!!anchorEl}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      PaperProps={{ sx: { borderRadius: '14px', mt: 0.5, width: 300, boxShadow: '0 10px 40px rgba(15,23,42,0.14)' } }}
    >
      <Typography sx={{ px: 2, pt: 1.5, pb: 0.5, fontSize: '0.7rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
        Set project status
      </Typography>
      <Box role="listbox" aria-label="Select new project status" sx={{ pb: 1 }}>
        {PROJECT_STATUSES.map((key) => {
          const meta = STATUS_META[key];
          const Icon = meta.icon;
          const selected = key === current;
          return (
            <ListItemButton
              key={key}
              role="option"
              aria-selected={selected}
              selected={selected}
              onClick={() => onSelect(key)}
              sx={{ py: 1, px: 2, '&.Mui-selected': { bgcolor: meta.bg } }}
            >
              <ListItemIcon sx={{ minWidth: 34 }}>
                <Box sx={{ width: 26, height: 26, borderRadius: '8px', bgcolor: meta.bg, border: `1px solid ${meta.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon size={14} color={meta.fg} />
                </Box>
              </ListItemIcon>
              <ListItemText
                primary={meta.label}
                secondary={meta.description}
                primaryTypographyProps={{ fontWeight: 700, fontSize: '0.85rem', color: '#1e293b' }}
                secondaryTypographyProps={{ fontSize: '0.7rem', color: '#94a3b8' }}
              />
              {selected && <CheckCircle2 size={15} color={meta.fg} />}
            </ListItemButton>
          );
        })}
      </Box>
    </Popover>
  );
};

/* ── Confirmation Dialog ─────────────────────────────────────────────────── */

export const StatusConfirmDialog = ({ open, versionName, oldStatus, newStatus, saving, onCancel, onConfirm }) => {
  const from = STATUS_META[normalizeStatus(oldStatus)];
  const to = STATUS_META[normalizeStatus(newStatus)];
  if (!open) return null;
  return (
    <Dialog open={open} onClose={saving ? undefined : onCancel} fullWidth maxWidth="xs"
      aria-labelledby="status-confirm-title" PaperProps={{ sx: { borderRadius: '16px' } }}>
      <DialogTitle id="status-confirm-title" sx={{ fontWeight: 800 }}>Update Project Status</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ color: '#475569', mb: 2.5 }}>
          Are you sure you want to change the project status{versionName ? <> of <b>{versionName}</b></> : null}?
        </Typography>
        <Box display="flex" alignItems="center" justifyContent="center" gap={1.5} mb={1}>
          <ProjectStatusChip status={oldStatus} />
          <ArrowRight size={18} color="#94a3b8" aria-label="changes to" />
          <ProjectStatusChip status={newStatus} />
        </Box>
        {normalizeStatus(newStatus) === 'COMPLETED' && (
          <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#b45309', bgcolor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '10px', p: 1.25, fontWeight: 600 }}>
            Completing this version closes its enrollment, opens the next version automatically
            and unlocks the next version for its students.
          </Typography>
        )}
        {normalizeStatus(oldStatus) === 'COMPLETED' && normalizeStatus(newStatus) !== 'COMPLETED' && (
          <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#991b1b', bgcolor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', p: 1.25, fontWeight: 600 }}>
            Reverting a Completed version requires Super Admin permission.
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onCancel} disabled={saving} sx={{ fontWeight: 700, color: '#64748b' }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ fontWeight: 700, borderRadius: '10px', bgcolor: to?.solid || '#843FEF', '&:hover': { bgcolor: to?.fg || '#6d28d9' } }}
        >
          {saving ? 'Updating…' : 'Update Status'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ── Project Timeline (Version Details page) ─────────────────────────────── */

export const ProjectStatusTimeline = ({ status, orientation = 'vertical' }) => {
  const currentKey = normalizeStatus(status);
  const currentIdx = PROJECT_STATUSES.indexOf(currentKey);
  const horizontal = orientation === 'horizontal';

  return (
    <Box
      role="list"
      aria-label="Project lifecycle timeline"
      sx={{ display: 'flex', flexDirection: horizontal ? 'row' : 'column', gap: horizontal ? 0 : 0.25, flexWrap: horizontal ? 'wrap' : 'nowrap' }}
    >
      {PROJECT_STATUSES.map((key, idx) => {
        const meta = STATUS_META[key];
        const Icon = meta.icon;
        const done = idx < currentIdx;
        const active = idx === currentIdx;
        const muted = idx > currentIdx;
        return (
          <Box key={key} role="listitem" aria-current={active ? 'step' : undefined}
            sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', minWidth: horizontal ? 130 : 'auto' }}>
            <Box display="flex" flexDirection="column" alignItems="center">
              <Box sx={{
                width: 34, height: 34, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: active ? meta.solid : done ? meta.bg : '#f8fafc',
                border: `2px solid ${active ? meta.solid : done ? meta.border : '#e2e8f0'}`,
                boxShadow: active ? `0 4px 12px ${meta.border}` : 'none',
                transition: 'all .2s ease'
              }}>
                {done
                  ? <CheckCircle2 size={16} color={meta.fg} aria-label={`${meta.label} — completed`} />
                  : <Icon size={16} color={active ? '#ffffff' : '#cbd5e1'} aria-hidden="true" />}
              </Box>
              {idx < PROJECT_STATUSES.length - 1 && !horizontal && (
                <Box sx={{ width: 2, height: 26, bgcolor: done ? meta.border : '#e2e8f0', my: 0.25, borderRadius: 1 }} />
              )}
            </Box>
            <Box sx={{ pb: horizontal ? 1 : 0.5, pr: horizontal ? 1 : 0 }}>
              <Typography sx={{ fontWeight: active ? 800 : 700, fontSize: '0.83rem', color: active ? meta.fg : done ? '#334155' : '#cbd5e1', lineHeight: 1.2, mt: 0.5 }}>
                {meta.label}
              </Typography>
              <Typography sx={{ fontSize: '0.68rem', color: active ? '#64748b' : done ? '#94a3b8' : '#e2e8f0', fontWeight: 500, maxWidth: 200 }}>
                {active ? meta.description : done ? 'Completed' : muted ? 'Upcoming' : ''}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

/* ── Status History Dialog ───────────────────────────────────────────────── */

export const StatusHistoryDialog = ({ open, onClose, versionId, versionName }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !versionId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await axios.get(
          `${API_BASE_URL}/api/nextgen-builder/admin/project-versions/${versionId}/status-history`,
          { params: adminIdentityParams() }
        );
        if (!cancelled) setRows(res.data?.data || []);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.message || 'Failed to load status history');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, versionId]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: '16px' } }}>
      <DialogTitle sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
        <History size={18} /> Status History{versionName ? ` — ${versionName}` : ''}
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={5}><CircularProgress size={26} /></Box>
        ) : error ? (
          <Typography color="error" sx={{ p: 3, fontWeight: 600 }}>{error}</Typography>
        ) : rows.length === 0 ? (
          <Typography sx={{ p: 4, color: '#94a3b8', textAlign: 'center', fontWeight: 600 }}>
            No status changes recorded yet
          </Typography>
        ) : (
          <Table size="small">
            <TableHead sx={{ bgcolor: '#f8fafc' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: '0.72rem' }}>OLD STATUS</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: '0.72rem' }}>NEW STATUS</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: '0.72rem' }}>UPDATED BY</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: '0.72rem' }}>UPDATED ON</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>{r.old_status ? <ProjectStatusChip status={r.old_status} /> : <Typography variant="caption" color="#94a3b8">—</Typography>}</TableCell>
                  <TableCell><ProjectStatusChip status={r.new_status} /></TableCell>
                  <TableCell><Typography variant="body2" fontWeight={600} color="#334155">{r.updated_by || 'admin'}</Typography></TableCell>
                  <TableCell>
                    <Typography variant="caption" color="#64748b" fontWeight={600}>
                      {r.created_at ? new Date(r.created_at).toLocaleString() : '—'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} sx={{ fontWeight: 700 }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

/** Shared API call — PATCH the new admin status endpoint (identity in body → CORS-proof). */
export const patchVersionStatus = (versionId, status) =>
  axios.patch(
    `${API_BASE_URL}/api/nextgen-builder/admin/project-versions/${versionId}/status`,
    { status, ...adminIdentityParams() }
  );
